/**
 * PATH: packages/ui/src/components/settings/NavigationTab.tsx
 * PURPOSE: Launch navigation customization from within settings
 *
 * WHY: Users expect navigation preferences to live inside the settings modal
 *      without leaving the hub.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────────┐    ┌─────────────────┐
 *   │ Load Prefs  │───▶│ Customize Layout │───▶│ Save to Backend │
 *   └─────────────┘    └──────────────────┘    └─────────────────┘
 *
 * DEPENDENCIES:
 *   - NavigationCustomization: existing customization panel
 *   - Backend API: /api/settings/navigation/
 */

import { useState, useEffect, useCallback } from 'react'
import { LayoutGrid, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react'
import { NavigationCustomization } from '../NavigationCustomization'
import { cn } from '../../lib/utils'
import { notifyNavCustomizationChanged } from '../../hooks/navCustomizationStorage'
import type { HubId, UserCustomization } from '../types'
import type { SettingsApiClient } from './types'

export interface NavigationTabProps {
  currentHub?: HubId
  apiClient?: SettingsApiClient
  organisationId?: string
  className?: string
}

// Storage key for fallback localStorage (backwards compatibility)
const STORAGE_KEY = 'quotech_nav_customization'

function migrateLegacyKeys(): Partial<UserCustomization> | null {
  try {
    const hubRaw = localStorage.getItem('customHubItems')
    const collapsed = localStorage.getItem('sidebarCollapsed')
    if (!hubRaw && collapsed === null) return null
    const hubs = hubRaw ? JSON.parse(hubRaw) : []
    const hubOrder = Array.isArray(hubs)
      ? hubs.filter((i: { enabled?: boolean }) => i.enabled !== false)
            .sort((a: { order?: number }, b: { order?: number }) => (a.order ?? 0) - (b.order ?? 0))
            .map((i: { id: string }) => i.id as HubId)
      : []
    return {
      hubOrder,
      sidebarCollapsed: collapsed === 'true',
    }
  } catch {
    return null
  }
}

function getLocalStorageCustomization(): Partial<UserCustomization> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) return JSON.parse(stored)
    // Migrate legacy keys on first load
    const legacy = migrateLegacyKeys()
    if (legacy) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(legacy))
      return legacy
    }
    return {}
  } catch {
    return {}
  }
}

function saveLocalStorageCustomization(data: Partial<UserCustomization>): void {
  try {
    // MERGE with the existing blob rather than overwrite. This tab only
    // owns a subset of the customization (hub order, pins, smart-order
    // flag); fields it doesn't model — notably `hiddenPages`, written by
    // the sidebar's right-click "Hide" via useNavCustomization — must
    // survive a save here. A full overwrite would silently un-hide items.
    const raw = localStorage.getItem(STORAGE_KEY)
    const existing = raw ? (JSON.parse(raw) as Partial<UserCustomization>) : {}
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...data }))
    // Same-tab notify so Smart ordering reflects a toggle immediately.
    notifyNavCustomizationChanged()
  } catch {
    // Storage unavailable
  }
}

export function NavigationTab({ currentHub, apiClient, organisationId, className }: NavigationTabProps) {
  const [panelOpen, setPanelOpen] = useState(false)
  const [customization, setCustomization] = useState<Partial<UserCustomization>>({})
  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [lastSynced, setLastSynced] = useState<Date | null>(null)

  // Load preferences from API on mount
  useEffect(() => {
    async function loadPreferences() {
      // If no API client, fall back to localStorage
      if (!apiClient) {
        setCustomization(getLocalStorageCustomization())
        return
      }

      setLoading(true)
      try {
        // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
        const url = organisationId 
          ? `/settings/navigation/?organisation_id=${organisationId}`
          : '/settings/navigation/'
        
        const response = await apiClient.get(url)
        const prefs = response.data?.preferences || response.preferences || {}
        
        // Map backend format to frontend format
        const mapped: Partial<UserCustomization> = {
          hubOrder: prefs.hub_order || [],
          pinnedPages: prefs.pinned_pages || [],
          navItemOrder: prefs.nav_item_order || {},
          aiAutoOrder: prefs.ai_auto_order ?? true,
          theme: prefs.theme || 'system',
          sidebarCollapsed: prefs.sidebar_collapsed || false,
          quickAccessShortcuts: prefs.quick_access_shortcuts || [],
        }
        
        setCustomization(mapped)
        setLastSynced(new Date())
        
        // Also update localStorage for offline access
        saveLocalStorageCustomization(mapped)
      } catch (error) {
        if (import.meta.env.DEV) console.error('[NavigationTab] Failed to load preferences:', error)
        // Fall back to localStorage
        setCustomization(getLocalStorageCustomization())
      } finally {
        setLoading(false)
      }
    }

    loadPreferences()
  }, [apiClient, organisationId])

  // Save preferences to API
  const handleSave = useCallback(async (next: Partial<UserCustomization>) => {
    setCustomization(next)
    
    // Always save to localStorage for offline access
    saveLocalStorageCustomization(next)
    
    // If no API client, we're done
    if (!apiClient) {
      return
    }

    setSaveStatus('saving')
    try {
      // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
      const url = organisationId 
        ? `/settings/navigation/?organisation_id=${organisationId}`
        : '/settings/navigation/'
      
      // Map frontend format to backend format
      const payload = {
        preferences: {
          hub_order: next.hubOrder || [],
          pinned_pages: next.pinnedPages || [],
          nav_item_order: next.navItemOrder || {},
          ai_auto_order: next.aiAutoOrder ?? true,
          theme: next.theme || 'system',
          sidebar_collapsed: next.sidebarCollapsed || false,
          quick_access_shortcuts: next.quickAccessShortcuts || [],
        }
      }
      
      await apiClient.put(url, payload)
      setSaveStatus('saved')
      setLastSynced(new Date())
      
      // Reset status after a delay
      setTimeout(() => setSaveStatus('idle'), 2000)
    } catch (error) {
      if (import.meta.env.DEV) console.error('[NavigationTab] Failed to save preferences:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }, [apiClient, organisationId])

  return (
    <div className={cn('space-y-4 max-w-2xl', className)}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Navigation</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Personalize your sidebar order, pins, and smart navigation.
          </p>
        </div>
        {/* Sync status indicator */}
        {apiClient && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {saveStatus === 'saving' && (
              <>
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>Saving...</span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-green-600">Saved</span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <AlertCircle className="w-3 h-3 text-red-500" />
                <span className="text-red-600">Failed to save</span>
              </>
            )}
            {saveStatus === 'idle' && lastSynced && (
              <span>Synced {lastSynced.toLocaleTimeString()}</span>
            )}
          </div>
        )}
      </div>

      <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400">
              <LayoutGrid className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-slate-700 dark:text-slate-200">Customize navigation</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Reorder hubs, pin pages, and enable AI-based sorting.
              </p>
            </div>
          </div>
          <button
            onClick={() => setPanelOpen(true)}
            disabled={loading}
            className={cn(
              "px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {loading ? 'Loading...' : 'Open'}
          </button>
        </div>
      </div>

      {/* Cloud sync notice */}
      {apiClient && (
        <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
          Your navigation preferences are synced across devices.
        </p>
      )}

      <NavigationCustomization
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        currentHub={currentHub}
        customization={customization}
        onSave={handleSave}
      />
    </div>
  )
}

export default NavigationTab
